import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  CircleOff,
  Loader2,
  Plus,
  RefreshCcw,
  ScrollText,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { orderPolicyProfilesApi } from "../../api/orderPolicyProfilesApi";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import { OrderFulfilmentEditor } from "./OrderFulfilmentPage";
import { OrderPricingEditor } from "./OrderPricingPage";
import { commerceSettingsStyles as styles } from "./commerceSettingsStyles";

const normalizeReadOption = option => ({
  ...option,
  description: null,
  version: null,
  revision: {
    id: option.revisionId,
    revisionNumber: option.revisionNumber,
  },
});

const readError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const modeLabel = mode => ({
  notRequired: "No delivery or pickup required",
  deliveryOnly: "Delivery only",
  pickupOnly: "Pickup only",
  deliveryOrPickup: "Delivery or pickup",
}[mode] || mode);

const tabLabel = tab => ({
  overview: "Overview",
  fulfilment: "Delivery & Pickup",
  pricing: "Taxes & Charges",
}[tab] || tab);

function StatusBadges({ profile }) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      {profile.isDefault ? (
        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
          Default
        </span>
      ) : null}
      <span className={`text-xs font-medium ${profile.isActive ? "text-emerald-700" : "text-slate-500"}`}>
        {profile.isActive ? "Active" : "Inactive"}
      </span>
    </span>
  );
}

function CreatePolicyDialog({ saving, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <section role="dialog" aria-modal="true" aria-labelledby="create-policy-title" className={styles.dialog}>
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 id="create-policy-title" className="font-semibold text-slate-900">Create Order Policy</h2>
            <p className="mt-1 text-sm text-slate-600">Create a reusable policy for an Order journey.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className={styles.iconButton}><X size={18} /></button>
        </header>
        <div className="grid gap-4 px-5 py-5">
          <label className="text-sm font-medium text-slate-800">
            Name
            <input autoFocus maxLength={120} value={name} onChange={event => setName(event.target.value)} className={styles.field} />
          </label>
          <label className="text-sm font-medium text-slate-800">
            Description <span className="font-normal text-slate-500">(optional)</span>
            <textarea maxLength={500} rows={3} value={description} onChange={event => setDescription(event.target.value)} className={styles.textarea} />
          </label>
        </div>
        <footer className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button type="button" onClick={onClose} className={styles.secondaryButton}>Cancel</button>
          <button type="button" disabled={!name.trim() || saving} onClick={() => onCreate({ name: name.trim(), description: description.trim() || null })} className={styles.primaryButton}>
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create Policy
          </button>
        </footer>
      </section>
    </div>
  );
}

function OverviewEditor({ profile, busy, onSave, onLifecycle }) {
  const [form, setForm] = useState({ name: profile.name, description: profile.description || "" });
  useEffect(() => {
    setForm({ name: profile.name, description: profile.description || "" });
  }, [profile.id, profile.version, profile.name, profile.description]);
  const changed = form.name.trim() !== profile.name || form.description.trim() !== (profile.description || "");

  return (
    <div className="grid gap-5">
      <section className={styles.card}>
        <header className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <h2 className="text-sm font-semibold text-slate-900">Policy details</h2>
        </header>
        <div className="grid gap-4 px-4 py-5 sm:grid-cols-2 sm:px-5">
          <label className="text-sm font-medium text-slate-800">
            Name
            <input maxLength={120} value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} className={styles.field} />
          </label>
          <div className="text-sm">
            <p className="font-medium text-slate-800">Current revision</p>
            <p className="mt-2 text-slate-700">Revision {profile.revision.revisionNumber}</p>
          </div>
          <label className="text-sm font-medium text-slate-800 sm:col-span-2">
            Description
            <textarea maxLength={500} rows={3} value={form.description} onChange={event => setForm(current => ({ ...current, description: event.target.value }))} className={styles.textarea} />
          </label>
        </div>
        <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
          <button type="button" disabled={!changed || !form.name.trim() || busy} onClick={() => onSave(form)} className={styles.primaryButton}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} {busy ? "Saving..." : "Save details"}
          </button>
        </div>
      </section>

      <section className={`${styles.card} px-4 py-4 sm:px-5`}>
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <div className="flex items-center gap-3"><h2 className="text-sm font-semibold text-slate-900">Policy status</h2><StatusBadges profile={profile} /></div>
            <p className="mt-2 text-sm text-slate-600">Deactivation prevents new use but does not change historical Orders or commitments.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!profile.isDefault && profile.isActive ? (
              <button type="button" disabled={busy} onClick={() => onLifecycle("default")} className={styles.secondaryButton}>Make default</button>
            ) : null}
            <button type="button" disabled={busy || profile.isDefault} onClick={() => onLifecycle(profile.isActive ? "deactivate" : "activate")} className={styles.secondaryButton}>
              {profile.isActive ? <CircleOff size={16} /> : <BadgeCheck size={16} />}
              {profile.isActive ? "Deactivate" : "Activate"}
            </button>
          </div>
        </div>
        {profile.isDefault ? <p className="mt-3 text-xs text-slate-500">Choose another active policy as default before deactivating this one.</p> : null}
      </section>
    </div>
  );
}

export default function OrderPoliciesPage() {
  const { can, hasAllAccess } = useAuth();
  const canManagePolicies = hasAllAccess || can(FK.SETTINGS_PROFILE_UPDATE);
  const canViewPricing = hasAllAccess || can(FK.COMMERCE_TRANSACTION_VIEW);
  const availableTabs = useMemo(() => [
    ...(canManagePolicies ? ["overview", "fulfilment"] : []),
    ...(canViewPricing ? ["pricing"] : []),
  ], [canManagePolicies, canViewPricing]);
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async preferredId => {
    setLoading(true);
    setError("");
    try {
      const rows = (await (canManagePolicies
        ? orderPolicyProfilesApi.list(true)
        : orderPolicyProfilesApi.listOptions(true))) || [];
      const visibleRows = canManagePolicies ? rows : rows.map(normalizeReadOption);
      setProfiles(visibleRows);
      setSelectedId(current => {
        const wanted = preferredId || current;
        return visibleRows.some(row => row.id === wanted)
          ? wanted
          : visibleRows.find(row => row.isDefault)?.id || visibleRows[0]?.id || null;
      });
    } catch (requestError) {
      setError(readError(requestError, "Order Policies could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [canManagePolicies]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!availableTabs.includes(tab)) setTab(availableTabs[0] || "pricing");
  }, [availableTabs, tab]);
  const selected = useMemo(
    () => profiles.find(profile => profile.id === selectedId) || null,
    [profiles, selectedId],
  );

  const replaceProfile = profile => {
    setProfiles(current => current.map(row => row.id === profile.id ? profile : row));
  };

  const authoritativeConflict = async () => {
    toast.error("This policy was changed by another user. The latest version has been reloaded.");
    await load(selectedId);
  };

  async function createProfile(values) {
    if (busy) return;
    setBusy(true);
    try {
      const created = await orderPolicyProfilesApi.create({
        ...values,
        fulfilmentMode: "notRequired",
        requireCheckoutReadyBeforeAcceptance: false,
        checkoutFlowDefinitionId: null,
        isDefault: false,
      });
      setCreating(false);
      await load(created.id);
      toast.success("Order Policy created.");
    } catch (requestError) {
      toast.error(readError(requestError, "Order Policy could not be created."));
    } finally {
      setBusy(false);
    }
  }

  async function saveOverview(form) {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const updated = await orderPolicyProfilesApi.update(selected.id, {
        expectedVersion: selected.version,
        name: form.name.trim(),
        description: form.description.trim() || null,
        fulfilmentMode: selected.revision.fulfilmentMode,
        requireCheckoutReadyBeforeAcceptance:
          selected.revision.requireCheckoutReadyBeforeAcceptance,
        checkoutFlowDefinitionId: selected.revision.checkoutFlowDefinitionId || null,
      });
      replaceProfile(updated);
      toast.success("Policy details saved.");
    } catch (requestError) {
      if (requestError?.response?.status === 409) await authoritativeConflict();
      else toast.error(readError(requestError, "Policy details could not be saved."));
    } finally {
      setBusy(false);
    }
  }

  async function changeLifecycle(action) {
    if (!selected || busy) return;
    setBusy(true);
    try {
      if (action === "default") await orderPolicyProfilesApi.setDefault(selected.id, selected.version);
      else if (action === "activate") await orderPolicyProfilesApi.activate(selected.id, selected.version);
      else await orderPolicyProfilesApi.deactivate(selected.id, selected.version);
      await load(selected.id);
      toast.success(action === "default" ? "Default Order Policy changed." : "Policy status updated.");
    } catch (requestError) {
      if (requestError?.response?.status === 409) await authoritativeConflict();
      else toast.error(readError(requestError, "Policy status could not be updated."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6">
      <header className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700"><ScrollText size={20} /></div>
          <div><h1 className="text-xl font-semibold text-slate-900">Order Policies</h1><p className="mt-1 max-w-2xl text-sm text-slate-600">Manage delivery, pickup, taxes, and charges for different Order journeys. The default policy is used when a journey does not select a specific policy.</p></div>
        </div>
        {canManagePolicies ? <button type="button" onClick={() => setCreating(true)} className={styles.primaryButton}><Plus size={17} /> Create Policy</button> : null}
      </header>

      {loading && profiles.length === 0 ? <div role="status" className="flex min-h-56 items-center justify-center text-slate-500"><Loader2 size={18} className="mr-2 animate-spin" /> Loading policies</div> : error ? <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"><p>{error}</p><button type="button" onClick={() => load()} className={`${styles.secondaryButton} mt-3`}><RefreshCcw size={15} /> Retry</button></div> : profiles.length === 0 ? <section className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center"><h2 className="font-semibold text-slate-900">No Order Policies yet</h2><p className="mt-2 text-sm text-slate-600">{canManagePolicies ? "Create a policy to configure an Order journey." : "No policies are available for taxes and charges."}</p></section> : (
        <div className="grid min-w-0 gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className={`${styles.card} h-fit overflow-hidden`} aria-label="Policy Profiles">
            <h2 className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Profiles</h2>
            <div className="divide-y divide-slate-200">
              {profiles.map(profile => <button type="button" key={profile.id} onClick={() => { setSelectedId(profile.id); setTab(availableTabs[0] || "pricing"); }} className={`block w-full px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-600 ${profile.id === selectedId ? "bg-emerald-50 shadow-[inset_3px_0_0_#047857]" : "hover:bg-slate-50"}`}><span className="block truncate text-sm font-medium text-slate-900">{profile.name}</span><span className="mt-1 block"><StatusBadges profile={profile} /></span>{profile.revision.fulfilmentMode ? <span className="mt-2 block truncate text-xs text-slate-500">{modeLabel(profile.revision.fulfilmentMode)}</span> : <span className="mt-2 block text-xs text-slate-500">Revision {profile.revision.revisionNumber}</span>}</button>)}
            </div>
          </aside>

          {selected ? <section className="min-w-0">
            <div className="mb-4"><div className="flex flex-wrap items-center gap-3"><h2 className="text-lg font-semibold text-slate-900">{selected.name}</h2><StatusBadges profile={selected} /></div>{selected.description ? <p className="mt-1 text-sm text-slate-600">{selected.description}</p> : null}</div>
            <nav className="mb-5 flex overflow-x-auto border-b border-slate-200" aria-label="Policy sections">{availableTabs.map(value => <button type="button" key={value} onClick={() => setTab(value)} className={`min-h-11 shrink-0 border-b-2 px-4 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500 ${tab === value ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900"}`}>{tabLabel(value)}</button>)}</nav>
            {canManagePolicies && tab === "overview" ? <OverviewEditor profile={selected} busy={busy} onSave={saveOverview} onLifecycle={changeLifecycle} /> : null}
            {canManagePolicies && tab === "fulfilment" ? <OrderFulfilmentEditor profile={selected} onProfileUpdated={replaceProfile} onConflict={authoritativeConflict} /> : null}
            {canViewPricing && tab === "pricing" ? <OrderPricingEditor profileId={selected.id} /> : null}
          </section> : null}
        </div>
      )}
      {canManagePolicies && creating ? <CreatePolicyDialog saving={busy} onClose={() => !busy && setCreating(false)} onCreate={createProfile} /> : null}
    </main>
  );
}
